import { useEffect, useRef, type HTMLAttributes, type PointerEvent } from "react";
import "./SpotlightCard.css";

// Adapted from React Bits SpotlightCard; source, license and changes: ATTRIBUTIONS.md.
export function SpotlightCard({ children, className = "", ...props }: HTMLAttributes<HTMLDivElement>): React.JSX.Element {
  const light = useRef<HTMLSpanElement>(null);
  const enabled = useRef(false);
  const frame = useRef(0);

  useEffect(() => {
    const preference = window.matchMedia("(hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)");
    const update = (): void => {
      enabled.current = preference.matches;
      if (!preference.matches && light.current !== null) light.current.style.opacity = "0";
    };
    update();
    preference.addEventListener("change", update);
    return () => {
      preference.removeEventListener("change", update);
      cancelAnimationFrame(frame.current);
    };
  }, []);

  function move(event: PointerEvent<HTMLDivElement>): void {
    const element = light.current;
    if (!enabled.current || element === null || event.pointerType === "touch") return;
    const bounds = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - bounds.left;
    const y = event.clientY - bounds.top;
    cancelAnimationFrame(frame.current);
    frame.current = requestAnimationFrame(() => {
      element.style.transform = `translate(${String(x - 180)}px, ${String(y - 180)}px)`;
      element.style.opacity = "1";
    });
  }

  function leave(): void {
    cancelAnimationFrame(frame.current);
    if (light.current !== null) light.current.style.opacity = "0";
  }

  return <div {...props} className={`dc-spotlight ${className}`} onPointerMove={move} onPointerLeave={leave}>
    <span ref={light} className="dc-spotlight-light" aria-hidden="true" />
    {children}
  </div>;
}
